//Reference: https://www.samuelliedtke.com/blog/react-table-tutorial-part-1/
import { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import {
  ColumnFiltersState,
  SortingState,
  useTable,
} from '@tanstack/react-table'
import Pagination from '@/components/common/table/Pagination'
import Filter from '@/components/common/table/Filter'
import DebouncedInput from '@/components/common/DebouncedInput'
import { SortIcon, SortUpIcon, SortDownIcon } from '@/components/common/Icons'
import {
  Action,
  BulkActionButton,
  BulkActionSelect,
} from '@/components/common/table/BulkAction'
import {
  TableDataType,
  ExportFilter,
  PrepareForExport,
  fuzzyFilter,
  tableWrapperFeatures,
  TableWrapperColumnDef,
} from '@/lib/table/table'
import { className } from '@/components/common/styles'

type TableWrapperProps<TData extends TableDataType> = {
  data: TData[]
  columns: TableWrapperColumnDef<TData>[]
  exportData?: ExportFilter[]
  deleteWithList: (ids: TData['id'][]) => Promise<{ success: boolean }> //DeleteWithList
  prepareForExport: PrepareForExport<TData>
  name: string
}

/*
(function(elmProto){
    if ('scrollTopMax' in elmProto) {
        return;
    }
    Object.defineProperties(elmProto, {
        'scrollTopMax': {
            get: function scrollTopMax() {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              return this.scrollHeight - this.clientTop;
            }
        },
        'scrollLeftMax': {
            get: function scrollLeftMax() {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              return this.scrollWidth - this.clientLeft;
            }
        }
    });
}
)(HTMLDivElement.prototype);
*/

const TableWrapper = <TData extends TableDataType>({
  data,
  columns,
  exportData,
  deleteWithList,
  prepareForExport,
  name,
}: TableWrapperProps<TData>) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const rerender = useReducer(() => ({}), {})[1]
  const [action, setAction] = useState('dummy' as Action)

  //Start scroll

  const tableRef = useRef<HTMLDivElement>(null)

  const [scrollPosition, setScrollPosition] = useState(0)
  const [scrollAtBorder, setScrollAtBorder] = useState('left')

  const handleScroll = useCallback(() => {
    // NOTE: scrollLeft caveats:
    // - behaviour is different for RTL (right-to-left)
    // - value is usually an integer, but may be a decimal (with display scaling on some browsers)
    // - minimum is 0 (but can be negative while bumping)
    // - maximum is dependent on display scaling
    if (tableRef.current) {
      const position = tableRef.current.scrollLeft
      const scrollWidth = tableRef.current.scrollWidth
      const clientWidth = tableRef.current.clientWidth
      const scrollLeftMax = scrollWidth - clientWidth
      if (position <= 0) {
        setScrollAtBorder('left')
      } else if (position >= scrollLeftMax - 1) {
        // NOTE: -1 for rounding errors
        setScrollAtBorder('right')
      } else {
        setScrollAtBorder('none')
      }
      setScrollPosition(position)
    }
  }, [])

  const handlerRef = useRef<(() => void) | null>(null)

  // @@TODO: 20250628 Disabled this when fixing Pagination for TanstackTable v8.
  /*
  useEffect(
    function setupListeners() {
      console.log('USE-EFFECT#1')
      const element = tableRef.current
      if (!element) {
        return
      }

      handlerRef.current = handleScroll

      const listener = () => {
        handlerRef.current?.()
      }

      // Prevent adding unlimited event listeners (due to hot reload in dev mode).
      element.addEventListener('scroll', listener, {
        capture: true,
        passive: true,
      })
      return function cleanupListeners() {
        element.removeEventListener('scroll', listener, { capture: true })
      }
    },
    [handleScroll],
  )
  //End scroll
  */

  const table = useTable({
    features: tableWrapperFeatures,
    columns,
    data,
    debugTable: false,
    globalFilterFn: fuzzyFilter,
    state: {
      columnFilters,
      globalFilter,
      sorting,
    },
    // NOTE: With: onColumnFiltersChange: setColumnFilters, pagination breaks (resets to page=0).
    // To fix this we set: autoResetPageIndex: false
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    autoResetPageIndex: false,
  })
  //Get information about displayed rows
  const filter = table.state.columnFilters
  // @@TODO: console.log('displayFilter=', filter)
  const filteredModel = table.getFilteredRowModel()
  // @@TODO: console.log('getFilteredRowModel=', filteredModel)
  // @@TODO: console.log('getFilteredRowModel.rows=', filteredModel.rows)
  // @@TODO: console.log('getFilteredRowModel.rows.length=', filteredModel.rows.length)

  let total = 0
  let totalData = 0
  if (data) {
    total = data.length
    totalData = data.length
  }
  if (filteredModel.rows.length) {
    total = filteredModel.rows.length
  }
  const pagination = table.state.pagination
  const pageIndex = pagination.pageIndex
  const pageCount = table.getPageCount()
  const start = pagination.pageIndex * pagination.pageSize + 1
  let end = start + pagination.pageSize - 1
  if (end > total) {
    end = total
  }

  // Correct pageIndex to counter autoResetPageIndex: false
  useEffect(() => {
    // If current page is out of bounds (e.g., after filtering),
    // clamp it to the last valid page.
    if (pageIndex >= pageCount) {
      table.setPageIndex(Math.max(0, pageCount - 1))
    }
  }, [
    pageCount, // re-run if total page count changes
    pageIndex, // or if current pageIndex changes
    table,
  ])

  const firstColumnFilterId = table.state.columnFilters[0]?.id
  const firstSortId = table.state.sorting[0]?.id

  // @@TODO: fullName
  useEffect(() => {
    if (firstColumnFilterId === 'name' && firstSortId !== 'name') {
      table.setSorting([{ id: 'name', desc: false }])
    }
  }, [firstColumnFilterId, firstSortId, table])

  return (
    <div className="px-4">
      <div className="pt-4">
        <DebouncedInput
          value={globalFilter}
          onChange={(value) => setGlobalFilter(String(value))}
          className="rounded-md shadow-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-black mb-2"
          placeholder="Search all columns..."
          aria-label="search all columns"
        />
        <div className="sm:flex sm:gap-x-2">
          <BulkActionSelect setAction={setAction} />
          action={action}
          <BulkActionButton<TData>
            data={data}
            selectedRowIds={table.state.rowSelection}
            action={action}
            deleteWithList={deleteWithList}
            prepareForExport={prepareForExport}
          />
        </div>
        <div className="h-2" />
        <div className="relative">
          <div className="relative flex w-full justify-between border-y-0.5 border-x-0.5 border-gray-200 dark:border-gray-800">
            <div
              className={`z-40 flex-none w-4 -mr-4 bg-gray-300 dark:bg-gray-700 transition duration-300 ${
                scrollAtBorder !== 'left'
                  ? 'opacity-50'
                  : 'opacity-0 -translate-x-2'
              }`}
            />
            <div ref={tableRef} className="flex overflow-auto">
              <table className="divide-y divide-gray-200 dark:divide-gray-800">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="align-top">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          className="group items-center px-2 py-3 text-xs font-medium"
                        >
                          {header.isPlaceholder ? null : header.id ===
                            'select' ? ( // @@TODO: === 'select' is constant
                            <div className="mt-4">
                              {table.FlexRender({ header })}
                            </div>
                          ) : (
                            <button
                              type="button"
                              {...{
                                className: header.column.getCanSort()
                                  ? 'flex cursor-pointer select-none'
                                  : 'flex accent-violet-600', // @@TODO: accent-violet-600 ?
                                onClick:
                                  header.column.getToggleSortingHandler(),
                              }}
                            >
                              {table.FlexRender({ header })}
                              {{
                                asc: <SortUpIcon className="flex w-4 h-4" />,
                                desc: <SortDownIcon className="flex w-4 h-4" />,
                              }[header.column.getIsSorted() as string] ?? // @@TODO: false || SortDirection (NOT: string)
                                (header.column.getCanSort() ? (
                                  <SortIcon className="flex w-4 h-4 opacity-0 group-hover:opacity-100" />
                                ) : null)}
                            </button>
                          )}
                          {header.column.getCanFilter() ? (
                            <Filter column={header.column} table={table} />
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white divide-y divide-gray-300 dark:divide-gray-600 border-t-4 border-b-4">
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={
                        row.getIsSelected()
                          ? 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-500'
                          : 'bg-white dark:bg-black hover:bg-gray-400 dark:hover:bg-gray-500'
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={
                            row.getIsSelected() && cell.column.id === 'select' // @@TODO: Fixed value
                              ? 'px-7 py-4 whitespace-nowrap accent-violet-600'
                              : 'px-7 py-4 whitespace-nowrap'
                          }
                        >
                          {row.getIsSelected() &&
                            cell.column.id === 'select' && (
                              <div className="absolute flex -mt-4 -ml-[38px] w-1 h-14 bg-black dark:bg-white rounded-md" />
                            )}
                          {table.FlexRender({ cell })}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              className={`z-40 flex-none w-4 -ml-4 bg-gray-300 dark:bg-gray-700 transition duration-300 ${
                scrollAtBorder !== 'right'
                  ? 'opacity-50'
                  : 'opacity-0 translate-x-2'
              }`}
            />
          </div>
        </div>
        <div className="h-2" />
        <Pagination table={table} name={name} />
        <div className="h-4" />
        <button type="button" onClick={() => rerender()} className={className}>
          Rerender
        </button>
        <div>
          Showing records {start} to {end} of {total}
          {filter.length
            ? ` filtered results (total: ${totalData})`
            : ' results'}
        </div>
        <div>
          {JSON.stringify({
            selectedRowIds: table.state.rowSelection,
          })}
        </div>
        <div>
          Number of selected records:{' '}
          {Object.keys(table.state.rowSelection).length}
        </div>
        <div className="h-4" />
      </div>
    </div>
  )
}

export default TableWrapper
